/**
 * Widget-registry — de enige plek waar een widget-type wordt aangemeld. Elke entry
 * beschrijft uiterlijk (titel/icoon/accent), grid-gedrag (default/min/max breedte,
 * toegestane hoogtes) en de render-/settings-componenten.
 *
 * Een nieuw widget-type toevoegen = één component + één entry hier. Zie
 * docs/widget-design-principles.md.
 */
import ShortcutsWidget, { ShortcutsSettings } from './ShortcutsWidget';
import AgendaWidget, { AgendaSettings } from './AgendaWidget';
import EmptyWidget from './EmptyWidget';
import SalesProspectsWidget, { SalesProspectsSettings } from './SalesProspectsWidget';
import BKEWidget from './BKEWidget';
import SnakeWidget from './SnakeWidget';
import TodayCard, { ClockSettings } from './TodayCard';
import ColorWidget from './ColorWidget';
import QRWidget from './QRWidget';
import ProjectsWidget, { ProjectsSettings } from './ProjectsWidget';
import MeetingNotesWidget from './MeetingNotesWidget';
import TodoWidget from './TodoWidget';
import FritsDigestWidget from './FritsDigestWidget';

// Header-weergave (titel/icoon) voor de geheime games in 'today'-stand: dan heten
// ze gewoon "Klok". Anders de eigen titel/icoon van het type.
const klokIfToday = (s) => (s.mode === 'today'
  ? { title: 'Klok', icon: 'schedule', accent: { bg: '#eceff0', color: '#767c7e' } }
  : null);

export const LAYOUT_VERSION = 1;

export const WIDGETS = {
  shortcuts: {
    type: 'shortcuts',
    title: 'Shortcuts',
    icon: 'bookmark',
    accent: { bg: '#d5e3fd', color: '#526075' },
    description: 'Snelkoppelingen naar je tools',
    defaultW: 3, minW: 1, maxW: 3,
    defaultH: 'auto', allowedHeights: ['auto', 'normal', 'tall'],
    skeleton: 'tiles',
    Component: ShortcutsWidget,
    Settings: ShortcutsSettings,
    defaultSettings: { hideDefaults: false },
  },
  agenda: {
    type: 'agenda',
    title: 'Afspraken van vandaag',
    icon: 'event',
    accent: { bg: '#fef0d9', color: '#b45309' },
    description: 'Je Outlook-afspraken van vandaag',
    defaultW: 1, minW: 1, maxW: 2,
    defaultH: 'normal', allowedHeights: ['compact', 'normal', 'tall'],
    skeleton: 'list',
    Component: AgendaWidget,
    Settings: AgendaSettings,
    defaultSettings: { showLocation: true, hideAllDay: false },
  },
  sales: {
    type: 'sales',
    title: 'Mijn prospects',
    icon: 'handshake',
    accent: { bg: '#dbcdff', color: '#635983' },
    description: 'Je sales-prospects + contact-alerts',
    defaultW: 2, minW: 1, maxW: 3,
    defaultH: 'normal', allowedHeights: ['compact', 'normal', 'tall'],
    skeleton: 'list',
    Component: SalesProspectsWidget,
    Settings: SalesProspectsSettings,
    defaultSettings: { eigenaar: 'auto' },
  },
  empty: {
    type: 'empty',
    title: 'Lege ruimte',
    icon: 'crop_free',
    accent: { bg: '#eceff0', color: '#767c7e' },
    description: 'Lege widget — opvulling/spacer',
    defaultW: 1, minW: 1, maxW: 3,
    defaultH: 'auto', allowedHeights: ['auto', 'compact', 'normal', 'tall'],
    skeleton: 'blank',
    blank: true, // transparant + geen header in normale weergave (zie WidgetShell)
    Component: EmptyWidget,
    defaultSettings: {},
  },
  clock: {
    type: 'clock',
    title: 'Klok',
    icon: 'schedule',
    accent: { bg: '#eceff0', color: '#767c7e' },
    description: 'Tijd, datum & jaartal',
    defaultW: 1, minW: 1, maxW: 2,
    defaultH: 'normal', allowedHeights: ['auto', 'compact', 'normal', 'tall'],
    skeleton: 'generic',
    Component: TodayCard,
    Settings: ClockSettings,
    defaultSettings: { showDigital: true, showWorkday: false, showVacation: false, workdayEnd: '17:00', vacationDate: '', vacationName: '' },
  },
  color: {
    type: 'color',
    title: 'Kleur Converter',
    icon: 'palette',
    accent: { bg: '#dbcdff', color: '#635983' },
    description: 'Kleur kiezen & converteren',
    fullPageHref: '/color-converter',
    defaultW: 1, minW: 1, maxW: 3,
    defaultH: 'normal', allowedHeights: ['auto', 'compact', 'normal', 'tall'],
    skeleton: 'generic',
    Component: ColorWidget,
    defaultSettings: {},
  },
  qr: {
    type: 'qr',
    title: 'QR-code Generator',
    icon: 'qr_code_2',
    accent: { bg: '#eceff0', color: '#556071' },
    description: 'Snel een QR-code maken',
    fullPageHref: '/qr-generator',
    defaultW: 1, minW: 1, maxW: 2,
    defaultH: 'normal', allowedHeights: ['auto', 'compact', 'normal', 'tall'],
    skeleton: 'generic',
    Component: QRWidget,
    defaultSettings: {},
  },
  projects: {
    type: 'projects',
    title: 'Mijn projecten',
    icon: 'folder_open',
    accent: { bg: '#d5e3fd', color: '#526075' },
    description: 'Actieve projecten waar jij aan werkt',
    fullPageHref: '/projects',
    defaultW: 1, minW: 1, maxW: 2,
    defaultH: 'normal', allowedHeights: ['compact', 'normal', 'tall'],
    skeleton: 'list',
    Component: ProjectsWidget,
    Settings: ProjectsSettings,
    defaultSettings: { alleenMijn: true },
  },
  meetings: {
    type: 'meetings',
    title: 'Meetings',
    icon: 'groups',
    accent: { bg: '#dbcdff', color: '#635983' },
    description: 'Je laatste opgenomen meetings (FF Recorder)',
    fullPageHref: '/MeetingDashboard',
    defaultW: 1, minW: 1, maxW: 2,
    defaultH: 'normal', allowedHeights: ['compact', 'normal', 'tall'],
    skeleton: 'list',
    Component: MeetingNotesWidget,
    defaultSettings: {},
  },
  fritsdigest: {
    type: 'fritsdigest',
    title: 'Dagelijkse check',
    icon: 'wb_sunny',
    accent: { bg: '#fef0d9', color: '#b45309' },
    description: 'GPT-inzichten over je projecten & deadlines (dagelijkse PL-check)',
    defaultW: 1, minW: 1, maxW: 2,
    defaultH: 'normal', allowedHeights: ['compact', 'normal', 'tall'],
    skeleton: 'generic',
    Component: FritsDigestWidget,
    defaultSettings: {},
  },
  todo: {
    type: 'todo',
    title: 'Mijn taken',
    icon: 'checklist',
    accent: { bg: '#fef0d9', color: '#b45309' },
    description: 'Open taken op prioriteit',
    fullPageHref: '/vandaag',
    defaultW: 1, minW: 1, maxW: 2,
    defaultH: 'normal', allowedHeights: ['compact', 'normal', 'tall'],
    skeleton: 'list',
    Component: TodoWidget,
    defaultSettings: {},
  },

  // ── Geheime widgets — alleen in de bibliotheek na de geheime code (secret: true).
  // Eenmaal geplaatst blijven ze gewoon werken (de game tot de gebruiker 'm verbergt).
  bke: {
    type: 'bke',
    title: 'Boter-kaas-en-eieren',
    icon: 'grid_3x3',
    accent: { bg: '#e0e7ff', color: '#6366f1' },
    description: 'Geheime game — verbergen = vandaag-klok',
    defaultW: 1, minW: 1, maxW: 1,
    defaultH: 'normal', allowedHeights: ['compact', 'normal', 'tall'],
    skeleton: 'generic',
    secret: true,
    displayFor: klokIfToday,
    Component: BKEWidget,
    defaultSettings: { mode: 'game' },
  },
  snake: {
    type: 'snake',
    title: 'Snake',
    icon: 'sports_esports',
    accent: { bg: '#dcfce7', color: '#16a34a' },
    description: 'Geheime game — verbergen = vandaag-klok',
    defaultW: 1, minW: 1, maxW: 1,
    defaultH: 'normal', allowedHeights: ['compact', 'normal', 'tall'],
    skeleton: 'generic',
    secret: true,
    displayFor: klokIfToday,
    Component: SnakeWidget,
    defaultSettings: { mode: 'game' },
  },
};

// Bibliotheek-lijsten: publieke widgets altijd; geheime alleen na de code.
export const WIDGET_LIST = Object.values(WIDGETS).filter((w) => !w.secret);
export const SECRET_WIDGETS = Object.values(WIDGETS).filter((w) => w.secret);

// Standaard-indeling voor een gebruiker zonder opgeslagen layout.
export const DEFAULT_LAYOUT = {
  version: LAYOUT_VERSION,
  widgets: [
    { i: 'shortcuts-default', type: 'shortcuts', w: 3, h: 'auto', settings: {} },
    { i: 'agenda-default', type: 'agenda', w: 1, h: 'normal', settings: { showLocation: true, hideAllDay: false } },
  ],
};
